import { BookMarked, FileBadge2, Star } from 'lucide-react';
import FormSection from '../../../components/common/FormSection';
import SimpleAlert from '../../../components/common/SimpleAlert';
import AdminLayout from '../../../components/layout/AdminLayout';
import { useNavigate, Link } from 'react-router-dom';
import DedicatedStatusDataCard from '../../../components/dataCards/DedicatedStatusDataCard';
import { useRplApi } from '../../../hooks/useRplApi';
import { useEffect, useState } from 'react';
import { ComplianceHistoryResponseDTO, CourseDTO, InvoiceDTO, MemberDTO, RplDTO, ScoreDTO } from '../../../services/apiClient';
import { ActivityTypeConstants } from '../../../constants/ActivityTypeConstants';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { getMemberId } from '../../../utils/tokenUtils';
import { useMemberApi } from '../../../hooks/useMemberApi';
import { useDedicatedStatusApi } from '../../../hooks/useDedicatedStatusApi';
import { useActivityApi } from '../../../hooks/useActivityApi';
import { CourseTypeConstants } from '../../../constants/CourseTypeConstants';
import { RplStatusConstants } from '../../../constants/RplStatusConstants';
import { CourseStatusConstants } from '../../../constants/CourseStatusConstants';
import Drawer from '../../../components/common/Drawer';
import { useCourseApi } from '../../../hooks/useCourseApi';
import Payment, {
  PaymentCallbacks,
  PaymentConfig,
} from '../../../components/drawer/Payment';
import { useFileApi } from '../../../hooks/useFileApi';
import EvaluationScoreForm from '../../../components/forms/EvaluationScoreForm';
import { useScoreApi } from '../../../hooks/useScoreApi';
import DedicatedStatusQuestions from '../../../components/drawer/DedicatedStatusQuestions';
import { FileViewer } from '../../../components/common/FileViewer';
import { getCourseFee } from '../../../utils/courseUtils';
import { ComponentConstants } from '../../../constants/ComponentConstants';
import { usePayFastApi } from '../../../hooks/usePayFastApi';
import { useInvoiceApi } from '../../../hooks/useInvoiceApi';
import { downloadFileFromUrl } from '../../../utils/downloadUtils';
import ButtonLegendCta from '../../../components/common/ButtonLegendCta';
import { getButtonLegendItems } from '../../../utils/buttonLegendDisplayUtils';
import ForfeitureDrawer from '../../../components/drawer/ForfeitureDrawer';

const legendItems = getButtonLegendItems('dedicatedStatus');
const FORM_SECTIONS = {
  dedicatedStatus: {
    id: 'dedicated-status',
    title: 'Dedicated Status',
  },
  applications: {
    id: 'applications',
    title: 'Applications',
  },
};

type DedicatedStatusItem = (RplDTO | CourseDTO) & {
  itemType: 'rpl' | 'course';
};

const DedicatedStatus: React.FC = () => {
  const memberId = getMemberId();
  const [member, setMember] = useState<MemberDTO | null>(null);
  const [isUnder16, setIsUnder16] = useState<boolean>(false);
  const [isMemberEligible, setIsMemberEligible] = useState<boolean>(true);
  const [selectedRpl, setSelectedRpl] = useState<RplDTO | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<CourseDTO | null>(null);
  const [items, setItems] = useState<DedicatedStatusItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isScoreSubmitting, setIsScoreSubmitting] = useState<boolean>(false);
  const [downloadingCertificateId, setDownloadingCertificateId] = useState<string | null>(null);
  const [downloadingForfeitureId, setDownloadingForfeitureId] = useState<string | null>(null);
  const [downloadingStudyMaterialId, setDownloadingStudyMaterialId] = useState<string | null>(null);
  const { search: searchRpls, getById: getRpl, edit: editRpl, remove: removeRpl, sendProofOfPaymentLink: sendRplProofOfPaymentLink, updateProofOfPayment: updateRplProofOfPayment, getForfeitureDocument: getRplForfeitureDocument, isLoading: isLoadingRpls, error: rplError } = useRplApi();
  const { search: searchCourses, getById: getCourse, edit: editCourse, remove: removeCourse, sendProofOfPaymentLink: sendCourseProofOfPaymentLink, updateProofOfPayment: updateCourseProofOfPayment, getStudyMaterial, getForfeitureDocument: getCourseForfeitureDocument, isLoading: isLoadingCourses, error: courseError } = useCourseApi();
  const { generateCertificate: generateDedicatedStatusCertificate } = useDedicatedStatusApi();
  const { getAllComplianceActivitiesHistory } = useActivityApi();

  const { uploadFile, getFileUrl } = useFileApi();
  const { create: createScore, isLoading } = useScoreApi();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isScoreDrawerOpen, setIsScoreDrawerOpen] = useState(false);
  const [isForfeitDrawerOpen, setIsForfeitDrawerOpen] = useState(false);
  const [forfeitItem, setForfeitItem] = useState<DedicatedStatusItem | null>(null);
  const [makingPayment, setMakingPayment] = useState<boolean>(false);
  const [selectedRplInvoice, setSelectedRplInvoice] = useState<InvoiceDTO | null>(null);
  const [selectedCourseInvoice, setSelectedCourseInvoice] = useState<InvoiceDTO | null>(null);
  const [isLoadingPaymentInvoice, setIsLoadingPaymentInvoice] = useState<boolean>(false);
  const [questionsCompleted, setQuestionsCompleted] = useState<boolean>(false);
  const [viewingFile, setViewingFile] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const [threeYearHistory, setThreeYearHistory] = useState<ComplianceHistoryResponseDTO | null>(null);

  // PayFast fee states
  const [rplPayFastFee, setRplPayFastFee] = useState<number | undefined>();
  const [rplTotalWithPayFastFee, setRplTotalWithPayFastFee] = useState<number | undefined>();
  const [coursePayFastFee, setCoursePayFastFee] = useState<number | undefined>();
  const [courseTotalWithPayFastFee, setCourseTotalWithPayFastFee] = useState<number | undefined>();

  const { calculateFee } = usePayFastApi();
  const { search: searchInvoices } = useInvoiceApi();

  const RPL = 'Rpl';
  const COURSE = 'Course';

  const navigate = useNavigate();
  const { getById, sendProofOfPaymentLink } = useMemberApi();

  const activeOptions =
    'hover:bg-primary hover:text-white hover:cursor-pointer';
  const inactiveOptions = 'opacity-35';

  const handleDedicatedStatusSelect = (selection: string) => {
    switch (selection) {
      case 'Course':
        navigate('/admin/courses/create');
        break;
      case 'Rpl':
        navigate('/admin/rpls/create');
        break;
      default:
        return;
    }
  };

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      //Fetching Rpls
      const rplResponse = await searchRpls({
        filters: {
          ...filters,
          isDeleted: false,
        },
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      //Fetching Courses
      const courseResponse = await searchCourses({
        filters: {
          ...filters,
          isDeleted: false,
        },
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      const rplData: RplDTO[] = rplResponse?.data?.results || [];
      const courseData: CourseDTO[] = courseResponse?.data?.results || [];

      // Exclude declined and forfeited applications from eligibility checks so members can re-apply
      const activeRplData = rplData.filter(
        (rpl) =>
          rpl.rplStatusId !== RplStatusConstants.DECLINED &&
          rpl.rplStatusId !== RplStatusConstants.DECLINED_WITH_FEEDBACK &&
          rpl.rplStatusId !== RplStatusConstants.FORFEITED,
      );
      const activeCourseData = courseData.filter(
        (course) =>
          course.courseStatusId !== CourseStatusConstants.DECLINED &&
          course.courseStatusId !== CourseStatusConstants.FORFEITED,
      );

      // Mark items with their type
      const rplsWithType = rplData.map((rpl) => ({
        ...rpl,
        itemType: 'rpl' as const,
      }));
      const coursesWithType = courseData.map((course) => ({
        ...course,
        itemType: 'course' as const,
      }));

      // Combine and sort by createdUtc
      const combinedItems = [...rplsWithType, ...coursesWithType].sort(
        (a, b) => {
          const dateA = new Date(a.createdUtc || 0).getTime();
          const dateB = new Date(b.createdUtc || 0).getTime();
          return dateB - dateA;
        },
      );

      setItems(combinedItems);

      const totalRplRecords = rplResponse?.data?.totalCount || 0;
      const totalCourseRecords = courseResponse?.data?.totalCount || 0;
      setTotalRecords(totalRplRecords + totalCourseRecords);

      const hasHunterAndSportsPersonApplication =
        activeRplData.some(
          (rpl) =>
            rpl.courseTypeId ===
            CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON,
        ) ||
        activeCourseData.some(
          (course) =>
            course.courseTypeId ===
            CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON,
        );

      const hasDedicatedHunterApplication =
        activeRplData.some(
          (rpl) => rpl.courseTypeId === CourseTypeConstants.DEDICATED_HUNTER,
        ) ||
        activeCourseData.some(
          (course) =>
            course.courseTypeId === CourseTypeConstants.DEDICATED_HUNTER,
        );

      const hasSportsPersonApplication =
        activeRplData.some(
          (rpl) =>
            rpl.courseTypeId === CourseTypeConstants.DEDICATED_SPORTS_PERSON,
        ) ||
        activeCourseData.some(
          (course) =>
            course.courseTypeId === CourseTypeConstants.DEDICATED_SPORTS_PERSON,
        );

      if (
        hasHunterAndSportsPersonApplication ||
        (hasDedicatedHunterApplication && hasSportsPersonApplication)
      ) {
        setIsMemberEligible(false);
      } else {
        setIsMemberEligible(true);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  useEffect(() => {
    // Fetch member data and check age
    const fetchMemberData = async () => {
      try {
        if (!memberId) {
          return;
        }
        const response = await getById(memberId!);
        if (response?.data) {
          setMember(response.data);

          // Calculate age from dateOfBirth
          if (response.data.dateOfBirth) {
            const birthDate = new Date(response.data.dateOfBirth);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();

            // Adjust age if birthday hasn't occurred this year
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }

            setIsUnder16(age < 16);
          }
        }
      } catch (error) {
        console.error('Error fetching member data:', error);
      }
    };

    fetchMemberData();
    fetchItems(currentPage, pageSize, {});

    // Fetch 3-year compliance history
    const fetchThreeYearHistory = async () => {
      try {
        // Retrieve 3 years of compliance history
        // Offset starts at 0 for current year
        var complianceYearsToFetch = 2;
        const response = await getAllComplianceActivitiesHistory(complianceYearsToFetch);
        if (response?.data) {
          setThreeYearHistory(response.data);
        }
      } catch (error) {
        console.error('Error fetching 3-year compliance history:', error);
      }
    };

    fetchThreeYearHistory();
  }, [currentPage, pageSize]);

  // Calculate PayFast fees for RPL when selected
  useEffect(() => {
    const calculateRplPayFastFees = async () => {
      if (!selectedRpl?.courseType?.rplFee || selectedRpl.courseType.rplFee <= 0) {
        setRplPayFastFee(undefined);
        setRplTotalWithPayFastFee(undefined);
        return;
      }

      try {
        const response = await calculateFee(selectedRpl.courseType.rplFee);
        if (response.data) {
          setRplPayFastFee(response.data.payFastFee);
          setRplTotalWithPayFastFee(response.data.totalWithFee);
        }
      } catch (error) {
        console.error('Error calculating RPL PayFast fees:', error);
        setRplPayFastFee(undefined);
        setRplTotalWithPayFastFee(undefined);
      }
    };

    if (selectedRpl) {
      calculateRplPayFastFees();
    } else {
      setRplPayFastFee(undefined);
      setRplTotalWithPayFastFee(undefined);
    }
  }, [selectedRpl]);

  // Calculate PayFast fees for Course when selected
  useEffect(() => {
    const calculateCoursePayFastFees = async () => {
      if (!selectedCourse) {
        setCoursePayFastFee(undefined);
        setCourseTotalWithPayFastFee(undefined);
        return;
      }

      const amount = getCourseFee(selectedCourse as CourseDTO);
      if (amount <= 0) {
        setCoursePayFastFee(undefined);
        setCourseTotalWithPayFastFee(undefined);
        return;
      }

      try {
        const response = await calculateFee(amount);
        if (response.data) {
          setCoursePayFastFee(response.data.payFastFee);
          setCourseTotalWithPayFastFee(response.data.totalWithFee);
        }
      } catch (error) {
        console.error('Error calculating Course PayFast fees:', error);
        setCoursePayFastFee(undefined);
        setCourseTotalWithPayFastFee(undefined);
      }
    };

    if (selectedCourse) {
      calculateCoursePayFastFees();
    } else {
      setCoursePayFastFee(undefined);
      setCourseTotalWithPayFastFee(undefined);
    }
  }, [selectedCourse]);

  const handleEdit = (item: any) => {
    if (isRpl(item)) {
      navigate(`/admin/rpls/${item.id}`);
    } else if (isCourse(item)) {
      navigate(`/admin/courses/${item.id}`);
    }
  };

  const handleDelete = async (item: any) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      if (isRpl(item)) {
        await removeRpl(item.id || '');
      } else if (isCourse(item)) {
        await removeCourse(item.id || '');
      }
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  // Fetches the item's existing invoice (if any) before opening the payment drawer, so
  // Payment.tsx always has an existingInvoiceId/existingInvoice from its very first render
  // and never falls back to blindly trusting a possibly-stale locally-computed fee.
  const handleMakePayment = async (item: any) => {
    setIsLoadingPaymentInvoice(true);
    try {
      if (isRpl(item)) {
        const response = await searchInvoices({
          filters: { rplId: item.id, isDeleted: false },
          page: 1,
          pageSize: 1,
          sortBy: 'CreatedUtc',
          sortDescending: true,
        });
        setSelectedRplInvoice(response?.data?.results?.[0] ?? null);
        setSelectedRpl(item);
      } else if (isCourse(item)) {
        const response = await searchInvoices({
          filters: { dedicatedCourseId: item.id, isDeleted: false },
          page: 1,
          pageSize: 1,
          sortBy: 'CreatedUtc',
          sortDescending: true,
        });
        setSelectedCourseInvoice(response?.data?.results?.[0] ?? null);
        setSelectedCourse(item);
      }
    } catch (error) {
      console.error('Error fetching invoice for payment:', error);
    } finally {
      setIsLoadingPaymentInvoice(false);
    }
    setMakingPayment(true);
    setIsDrawerOpen(true);
  };

  const handlePaymentComplete = () => {
    setMakingPayment(false);

    setSelectedCourse(null);
    setSelectedRpl(null);
    setSelectedRplInvoice(null);
    setSelectedCourseInvoice(null);
    fetchItems(currentPage, pageSize, {});
  };

  const handleProofUploaded = async (fileRecordId: string, type: 'rpl' | 'course', id: string,) => {

    try {
      if (type === 'rpl') {
        await updateRplProofOfPayment(id, fileRecordId);
      } else if (type === 'course') {
        await updateCourseProofOfPayment(id, fileRecordId);
      }
    } catch (error) {
      console.error('Error handling proof of payment file:', error);
    }

    setMakingPayment(false);
    setSelectedCourse(null);
    setSelectedRpl(null);
    handleDrawerClose();
    fetchItems(currentPage, pageSize, {});
  };

  const handlePaymentCancel = () => {
    setMakingPayment(false);
    setSelectedRpl(null);
    setSelectedCourse(null);
    setSelectedRplInvoice(null);
    setSelectedCourseInvoice(null);
    setIsDrawerOpen(false);
  };

  const handleUploadLater = async () => {
    try {
      if (selectedRpl?.id) {
        await sendRplProofOfPaymentLink(selectedRpl.id);
      } else if (selectedCourse?.id) {
        await sendCourseProofOfPaymentLink(selectedCourse.id);
      }
    } catch (error) {
      console.error('Error sending proof of payment link:', error);
    } finally {
      handlePaymentCancel();
    }
  };

  const handleDownloadStudyMaterial = async (item: DedicatedStatusItem) => {
    setDownloadingStudyMaterialId(item.id ?? null);
    try {
      // Ensure the item is a course
      if (!isCourse(item)) {
        console.error('Item is not a course');
        return;
      }

      // Check if material has already been downloaded
      if (!item.timeMaterialDownloaded) {
        // Create the updated course with the download time
        const updatedCourse = {
          ...item,
          timeMaterialDownloaded: new Date(),
        };

        // Call handleMergeAndUpdate with the updated course data
        await handleMergeAndUpdate(updatedCourse);

        // Refresh the items list to show the updated data
        await fetchItems(currentPage, pageSize, {});
      }

      // Get the study material SAS URL
      const studyMaterialUrl = await getStudyMaterial(item.id!);

      // Trigger download (handles mobile/PWA automatically)
      if (studyMaterialUrl) {
        await downloadFileFromUrl(
          studyMaterialUrl,
          `study-material-${item.id}.pdf`,
          () => {
            console.log('Study material downloaded successfully');
          },
          (error) => {
            console.error('Failed to download study material:', error);
            alert(`Error downloading study material: ${error.message}`);
          }
        );
      }
    } catch (error) {
      console.error('Error downloading study material:', error);
      const errorMessage = error instanceof Error ? error.message : 'Please try again.';
      alert(`Error downloading study material: ${errorMessage}`);
    } finally {
      setDownloadingStudyMaterialId(null);
    }
  };

  const isRpl = (
    item: DedicatedStatusItem,
  ): item is RplDTO & { itemType: 'rpl' } => {
    return item.itemType === 'rpl';
  };

  const isCourse = (
    item: DedicatedStatusItem,
  ): item is CourseDTO & { itemType: 'course' } => {
    return item.itemType === 'course';
  };

  const handleMergeAndUpdate = async (
    courseToUpdate?: CourseDTO,
    rplToUpdate?: RplDTO,
  ) => {
    // Use passed parameters or fall back to state for backward compatibility
    const courseData = courseToUpdate || selectedCourse;
    const rplData = rplToUpdate || selectedRpl;

    if (rplData) {
      const latestRplResponse = await getRpl(rplData.id!);
      const latestRplData = latestRplResponse?.data;
      if (!latestRplData) return;

      // Only merge properties that are not null/undefined in the RPL data
      const mergedRplData = { ...latestRplData };
      Object.keys(rplData).forEach((key) => {
        if (
          rplData[key as keyof RplDTO] !== null &&
          rplData[key as keyof RplDTO] !== undefined
        ) {
          (mergedRplData as any)[key] = rplData[key as keyof RplDTO];
        }
      });
      await editRpl(rplData.id!, mergedRplData);
    } else if (courseData) {
      const latestCourseResponse = await getCourse(courseData.id!);
      const latestCourseData = latestCourseResponse?.data;
      if (!latestCourseData) return;

      // Only merge properties that are not null/undefined in the course data
      const mergedCourseData = { ...latestCourseData };
      Object.keys(courseData).forEach((key) => {
        if (
          courseData[key as keyof CourseDTO] !== null &&
          courseData[key as keyof CourseDTO] !== undefined
        ) {
          (mergedCourseData as any)[key] = courseData[key as keyof CourseDTO];
        }
      });
      await editCourse(courseData.id!, mergedCourseData);
    }
  };

  const handleDrawerClose = () => {
    setMakingPayment(false);
    setSelectedRpl(null);
    setSelectedCourse(null);
    setIsDrawerOpen(false);
  };

  const handleScoreSubmit = async (data: ScoreDTO) => {
    try {
      await createScore(data);
      handleScoreDrawerClose();
      window.location.href = '/admin/dedicatedstatus';
    } catch (error) {
      console.error('Error creating Score:', error);
    } finally {
      setIsScoreSubmitting(false);
    }
  };

  const handleScoreDrawerOpen = (item: any) => {
    if (isRpl(item)) {
      setSelectedRpl(item);
    } else if (isCourse(item)) {
      setSelectedCourse(item);
    }
    setIsScoreDrawerOpen(true);
  };

  const handleScoreDrawerClose = () => {
    setSelectedCourse(null);
    setIsScoreDrawerOpen(false);
    setQuestionsCompleted(false); // Reset when drawer closes
  };

  const handleQuestionsComplete = () => {
    setQuestionsCompleted(true);
  };

  const getCertificateType = (courseTypeId: string): string => {
    const typeMap: Record<string, string> = {
      [CourseTypeConstants.DEDICATED_HUNTER]: 'Hunter',
      [CourseTypeConstants.DEDICATED_SPORTS_PERSON]: 'Sport',
    };
    return typeMap[courseTypeId] ?? 'Unknown';
  };

  const generateCertificateUrl = async (item: DedicatedStatusItem): Promise<string> => {
    return await generateDedicatedStatusCertificate(item.courseTypeId!, item.id!);
  };

  const handleForfeit = (item: DedicatedStatusItem) => {
    setForfeitItem(item);
    setIsForfeitDrawerOpen(true);
  };

  const handleForfeitSuccess = () => {
    setIsForfeitDrawerOpen(false);
    setForfeitItem(null);
    fetchItems(currentPage, pageSize, {});
  };

  const handleForfeitCancel = () => {
    setIsForfeitDrawerOpen(false);
    setForfeitItem(null);
  };

  const handleDownloadForfeitureDocument = async (item: DedicatedStatusItem) => {
    setDownloadingForfeitureId(item.id ?? null);
    try {
      const url = isRpl(item)
        ? await getRplForfeitureDocument(item.id!)
        : await getCourseForfeitureDocument(item.id!);

      setViewingFile({
        name: `Forfeiture-Document-${item.id}.pdf`,
        url,
      });
    } catch (error) {
      console.error('Error downloading forfeiture document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Please try again.';
      alert(`Error downloading forfeiture document: ${errorMessage}`);
    } finally {
      setDownloadingForfeitureId(null);
    }
  };

  const handleDownloadCertificate = async (item: DedicatedStatusItem) => {
    setDownloadingCertificateId(item.id ?? null);
    try {
      const certificateUrl = await generateCertificateUrl(item);
      const certificateType = getCertificateType(item.courseTypeId!);
      const filename = `Dedicated-${certificateType}-Certificate(${item.id ?? ''}).pdf`;

      setViewingFile({
        name: filename,
        url: certificateUrl,
      });

    } catch (error) {
      console.error('Error downloading certificate:', error);
      const errorMessage = error instanceof Error ? error.message : 'Please try again.';
      alert(`Error downloading certificate: ${errorMessage}`);
    } finally {
      setDownloadingCertificateId(null);
    }
  };

  return (
    <AdminLayout
      activeMenuItem={'/admin/dedicatedstatus'}
      breadcrumbs={[
        { label: 'Admin', href: '#' },
        { label: 'Dedicated Status', href: '#' },
      ]}
      allowedRoles={['Admin', 'User']}
      requiredPermission="Manage.Activity.View"
    >
      <>
        <FormSection
          id={FORM_SECTIONS.dedicatedStatus.id}
          title={FORM_SECTIONS.dedicatedStatus.title}
        >
          {isUnder16 ? (
            <SimpleAlert
              message={
                <>
                  You need to be 16 years or older to apply for Dedicated Status, please{' '}
                  <Link to="/contact" className="underline hover:text-primary-dark">
                    contact
                  </Link>{' '}
                  the office for any questions.
                </>
              }
              variant="warning"
            />
          ) : (
            <>
              <div className="flex items-center mb-10 col-span-full">
                <FileBadge2 className="mr-3 text-primary" size={50} />
                <span className="font-bold text-xl">
                  How can we help you achieve dedicated status?
                </span>
              </div>
              <div className="col-span-full">
                <span className="font-bold text-xl">Select an option below</span>
                <div
                  className={`flex items-center border-2 border-primary rounded-lg p-5 my-5 ${isMemberEligible ? activeOptions : inactiveOptions}`}
                  onClick={() =>
                    isMemberEligible ? handleDedicatedStatusSelect(COURSE) : {}
                  }
                >
                  <BookMarked className="mr-3 text-primary" size={40} />
                  <div className="flex flex-col">
                    <span className="font-bold">
                      Enrol for the Natshoot Dedicated Course
                    </span>
                    <span>
                      Enrol for the Natshoot Dedicated Course for a new
                      qualification
                    </span>
                  </div>
                </div>
                <div
                  className={`flex items-center border-2 border-primary rounded-lg p-5 my-5 ${isMemberEligible ? activeOptions : inactiveOptions}`}
                  onClick={() =>
                    isMemberEligible ? handleDedicatedStatusSelect(RPL) : {}
                  }
                >
                  <Star className="mr-3 text-primary" size={40} />
                  <div className="flex flex-col">
                    <span className="font-bold">
                      Apply for Recognition of Prior Learning
                    </span>
                    <span>
                      Apply to be awarded dedicated status on previous qualification
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </FormSection>

        {!isUnder16 && (
          <FormSection
            id={FORM_SECTIONS.applications.id}
            title={FORM_SECTIONS.applications.title}
            headerActions={items.length > 0 ? <ButtonLegendCta items={legendItems} /> : undefined}
            columns={1}
          >
            {items.length === 0 ? (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
                <h3 className="text-lg font-semibold text-yellow-900 mb-4">Dear Member</h3>
                <p className="text-yellow-800 mb-4">
                  You currently have <span className="underline">no dedicated status applications</span> listed, if you have been awarded dedicated status on a previous qualification at another association or would like to apply for the Natshoot (NHSA) dedicated course, please select an option above.
                </p>
                <p className="text-yellow-800">
                  For any questions or topics relating to Dedicated Status use the help ? icon at the top right corner.
                </p>
              </div>
            ) : (
              <DedicatedStatusDataCard
                data={items}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                handleMakePayment={handleMakePayment}
                handleDownloadStudyMaterial={handleDownloadStudyMaterial}
                handleScoreDrawerOpen={handleScoreDrawerOpen}
                handleDownloadCertificate={handleDownloadCertificate}
                handleDownloadForfeitureDocument={handleDownloadForfeitureDocument}
                handleForfeit={handleForfeit}
                currentPage={currentPage}
                totalPages={Math.ceil(totalRecords / pageSize)}
                setCurrentPage={setCurrentPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                isLoading={
                  (isLoadingRpls || isLoadingCourses) &&
                  !downloadingCertificateId &&
                  !downloadingForfeitureId &&
                  !downloadingStudyMaterialId
                }
                downloadingCertificateId={downloadingCertificateId}
                downloadingForfeitureId={downloadingForfeitureId}
                downloadingStudyMaterialId={downloadingStudyMaterialId}
                threeYearHistory={threeYearHistory}
              />
            )}
          </FormSection>
        )}

        <Drawer
          isOpen={isScoreDrawerOpen}
          onClose={handleScoreDrawerClose}
          title="Submit Practical"
          size="large"
        >
          {!questionsCompleted ? (
            <DedicatedStatusQuestions
              answers={{}}
              onClose={handleScoreDrawerClose}
              onComplete={handleQuestionsComplete}
              isHuntingCourse={
                selectedCourse?.courseTypeId === CourseTypeConstants.DEDICATED_HUNTER ||
                selectedCourse?.courseTypeId === CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON
              }
            />
          ) : (
            <EvaluationScoreForm
              initialValues={{}}
              course={selectedCourse ?? {}}
              onSubmit={handleScoreSubmit}
              onCancel={handleScoreDrawerClose}
              isLoading={isScoreSubmitting}
            />
          )}
        </Drawer>

        <Drawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          title="Make a Payment"
        >
          <div className="space-y-4">
            {isLoadingPaymentInvoice && (
              <p className="text-sm text-gray-500">Loading payment details...</p>
            )}

            {/* RPL Payment */}
            {!isLoadingPaymentInvoice && selectedRpl?.member &&
              (() => {
                const paymentConfig: PaymentConfig = {
                  amount: selectedRpl.courseType?.rplFee ?? 0,
                  itemName: `RPL Application - ${selectedRpl.courseType?.title}`,
                  description: 'Recognition of Prior Learning Application',
                  reference: selectedRpl.member?.idPassportNumber ?? '',
                  payFastTransactionFee: rplPayFastFee,
                  totalWithPayFastFee: rplTotalWithPayFastFee,
                };

                const paymentCallbacks: PaymentCallbacks = {
                  onPaymentSuccess: handlePaymentComplete,
                  onProofUploaded: (fileRecordId) =>
                    handleProofUploaded(fileRecordId, 'rpl', selectedRpl.id!),
                  onProofPending: handleUploadLater,
                  onCancel: handlePaymentCancel,
                };

                return (
                  <Payment
                    member={selectedRpl.member}
                    rpl={selectedRpl}
                    componentId={ComponentConstants.RPL}
                    paymentConfig={paymentConfig}
                    callbacks={paymentCallbacks}
                    title="RPL Payment"
                    showMemberDetails={true}
                    type="rpl"
                    existingInvoiceId={selectedRplInvoice?.id}
                    existingInvoice={selectedRplInvoice ?? undefined}
                  />
                );
              })()}

            {/* Course Payment */}
            {!isLoadingPaymentInvoice && selectedCourse?.member &&
              (() => {
                // Get amount based on course material type //TODO
                const amount = getCourseFee(selectedCourse as CourseDTO);

                const paymentConfig: PaymentConfig = {
                  amount,
                  itemName: `Course Application - ${selectedCourse.courseType?.title}`,
                  description: `Course Application - ${selectedCourse.courseMaterialType?.title}`,
                  reference: selectedCourse.member?.idPassportNumber ?? '',
                  payFastTransactionFee: coursePayFastFee,
                  totalWithPayFastFee: courseTotalWithPayFastFee,
                };

                const paymentCallbacks: PaymentCallbacks = {
                  onPaymentSuccess: handlePaymentComplete,
                  onProofUploaded: (fileRecordId) =>
                    handleProofUploaded(fileRecordId, 'course', selectedCourse.id!),
                  onProofPending: handleUploadLater,
                  onCancel: handlePaymentCancel,
                };

                return (
                  <Payment
                    member={selectedCourse.member}
                    course={selectedCourse}
                    componentId={ComponentConstants.DEDICATED_COURSES}
                    paymentConfig={paymentConfig}
                    callbacks={paymentCallbacks}
                    title="Course Payment"
                    showMemberDetails={true}
                    type="course"
                    existingInvoiceId={selectedCourseInvoice?.id}
                    existingInvoice={selectedCourseInvoice ?? undefined}
                  />
                );
              })()}
          </div>
        </Drawer>

        <Drawer
          isOpen={isForfeitDrawerOpen}
          onClose={handleForfeitCancel}
          title="Forfeit Dedicated Status"
        >
          {forfeitItem && (
            <ForfeitureDrawer
              item={forfeitItem}
              onSuccess={handleForfeitSuccess}
              onCancel={handleForfeitCancel}
            />
          )}
        </Drawer>

        {viewingFile && (
          <FileViewer
            fileName={viewingFile.name}
            sasUrl={viewingFile.url}
            onClose={() => setViewingFile(null)}
          />
        )}
      </>
    </AdminLayout>
  );
};

export default DedicatedStatus;
